/**
 * stickyQuery — remember an applied filter tree for the life of a browser tab.
 *
 * Feedback #65: "when creating complex filtering on deals page, need to either
 * open a specific deal in a new tab or be able to go back to filtered results
 * so we won't have to redo the filtering". The filter tree lived in component
 * state, so navigating to a deal unmounted the list and threw it away — and the
 * filtering people are asking about is the expensive kind, several chips deep.
 *
 * `sessionStorage`, NOT `localStorage`, and the distinction is the whole design:
 *
 *   - It is per TAB, so opening a deal in a second tab and filtering differently
 *     there does not reach back and rewrite the first tab's list.
 *   - It dies with the tab. A filter is a transient working state, not a
 *     preference: coming in tomorrow to a deals grid silently showing four
 *     chips from last week is a worse bug than the one this fixes. Contrast
 *     `uiPrefs`, which is deliberately `localStorage` — a collapsed panel IS a
 *     preference.
 *
 * Never throws. Storage is unavailable in some private-mode configurations and
 * can be over quota, and a stored value can be anything if someone edits it —
 * so every path falls back to "nothing stored" rather than taking the page
 * down. Same contract as `uiPrefs`, and for the same reason.
 *
 * Not a security boundary and not data: a tampered value can only produce a
 * filter query, which the server validates and authorizes exactly as it does
 * one the user typed.
 */

/** Namespaced so these never collide with the app's other stored values. */
const PREFIX = "bd.query.";

/** The deals list's applied filter tree. */
export const DEALS_QUERY = "deals";

/**
 * Is this a filter tree worth storing — i.e. one that actually narrows?
 *
 * An empty tree is stored as *nothing*, so "no filter" and "never visited"
 * are the same state. Otherwise clearing the filters would leave an empty
 * query object behind that reads as a restore on the next mount.
 *
 * @param {unknown} query
 * @returns {boolean}
 */
const isMeaningful = (query) =>
  Boolean(query && Array.isArray(query.rules) && query.rules.length > 0);

/**
 * The stored filter tree for `key`, or `null` when there is none.
 *
 * Shape is checked, not trusted: anything that is not an object carrying a
 * `rules` array is treated as absent. A half-written or hand-edited value
 * must not reach the filter panel, which would render chips from it.
 *
 * @param {string} key
 * @returns {object|null}
 */
export const readQuery = (key) => {
  try {
    const raw = window.sessionStorage?.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isMeaningful(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Store `query` under `key`, or forget it when the query no longer narrows
 * anything. Writing an empty tree is how a caller clears the slot — there is
 * no separate remove, because "the user reset their filters" and "there is
 * nothing to restore" are the same fact.
 *
 * @param {string} key
 * @param {object|null|undefined} query
 */
export const writeQuery = (key, query) => {
  try {
    if (!isMeaningful(query)) {
      window.sessionStorage?.removeItem(PREFIX + key);
      return;
    }
    window.sessionStorage?.setItem(PREFIX + key, JSON.stringify(query));
  } catch {
    // Storage unavailable or full; the filter lasts for this page view only,
    // which is exactly the behaviour that predates this module.
  }
};
